import React, { useState, useRef, useEffect } from 'react';
import './GreekPanel.css';
import { decodeParsingCode } from '../utils/greekParsing';
import strongsGreek from '../data/strongs-greek.json';

/**
 * GreekPanel
 *
 * Displays the Textus Receptus parsed Greek text for one NT chapter.
 * Each word is a clickable chip — clicking it shows a detail card with
 * the Strong's number and full grammatical parsing.
 *
 * Props:
 *   chapterData      — array of verses; each verse is an array of word objects
 *                      { w: string, s: string, p: string, tf?: string }
 *   bookName         — English name of the current book (e.g. "John")
 *   chapterNum       — 1-based chapter number
 *   onClose          — callback to close the panel
 *   onVerseHighlight — callback(verseNum|null) called when a word is clicked;
 *                      highlights the corresponding verse in the translated text
 */
function GreekPanel({ chapterData, bookName, chapterNum, onClose, onVerseHighlight }) {
  const [selectedWord, setSelectedWord] = useState(null); // word object or null
  const detailRef = useRef(null);

  // Clear selection (and any highlight) when chapter changes
  useEffect(() => {
    setSelectedWord(null);
    onVerseHighlight?.(null, null);
  }, [bookName, chapterNum]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll detail card into view when a word is selected
  useEffect(() => {
    if (selectedWord && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedWord]);

  const handleWordClick = (word, verseNum) => {
    const isDeselect = selectedWord === word;
    setSelectedWord(isDeselect ? null : word);
    if (isDeselect) {
      onVerseHighlight?.(null, null);
    } else {
      // Split kjv_def "word, saying, matter" into ["word", "saying", "matter"]
      const glossRaw = strongsGreek[word.s] || '';
      const terms = glossRaw.split(',').map(t => t.trim()).filter(Boolean);
      onVerseHighlight?.(verseNum, terms);
    }
  };

  const decoded = selectedWord ? decodeParsingCode(selectedWord.p) : null;

  if (!chapterData) {
    return (
      <div className="Greek-panel">
        <div className="Greek-panel-header">
          <span className="Greek-panel-title">α Greek NT</span>
          <button className="Greek-panel-close" onClick={onClose} aria-label="Close Greek panel">✕</button>
        </div>
        <div className="Greek-panel-empty">
          No Greek text available for this book.<br />
          <small>The Greek NT only covers Matthew – Revelation.</small>
        </div>
      </div>
    );
  }

  return (
    <div className="Greek-panel">
      {/* ── Header ── */}
      <div className="Greek-panel-header">
        <span className="Greek-panel-title">
          <span className="Greek-alpha-badge">α</span>
          {bookName} {chapterNum}
        </span>
        <button className="Greek-panel-close" onClick={onClose} aria-label="Close Greek panel">✕</button>
      </div>

      {/* ── Word detail card ── */}
      {selectedWord && decoded && (
        <div className="Greek-word-detail" ref={detailRef}>
          <span className="Greek-detail-word">{selectedWord.w}</span>
          <div className="Greek-detail-meta">
            <span className="Greek-strongs">{selectedWord.s}</span>
            <span className="Greek-pos">{decoded.pos}</span>
          </div>
          {decoded.details.length > 0 && (
            <div className="Greek-detail-tags">
              {decoded.details.map((d, i) => (
                <span key={i} className="Greek-tag">{d}</span>
              ))}
            </div>
          )}
          {selectedWord.tf && (
            <span className="Greek-tenseform">Tense form: {selectedWord.tf}</span>
          )}
        </div>
      )}

      {/* ── Verse list ── */}
      <div className="Greek-verses">
        {chapterData.map((verseWords, vIdx) => (
          <div key={vIdx} className="Greek-verse">
            <span className="Greek-verse-num">{vIdx + 1}</span>
            <div className="Greek-words">
              {verseWords.map((word, wIdx) => (
                <button
                  key={wIdx}
                  className={`Greek-word-chip${selectedWord === word ? ' active' : ''}`}
                  onClick={() => handleWordClick(word, vIdx + 1)}
                  title={`${word.s} ${word.p || ''}`}
                >
                  {word.w}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GreekPanel;
